if (!customElements.get('hynox-exploded')) {
  customElements.define(
    'hynox-exploded',
    class HynoxExploded extends HTMLElement {
      connectedCallback() {
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.update = this.update.bind(this);
        if (this.reducedMotion.matches) {
          this.style.setProperty('--exploded-progress', 1);
          this.style.setProperty('--assembled-opacity', 0);
          this.style.setProperty('--exploded-opacity', 1);
          this.setSteps(1);
          return;
        }
        window.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update, { passive: true });
        this.update();
      }

      setSteps(progress) {
        const ranges = [0.12, 0.34, 0.55, 0.74];
        ranges.forEach((start, index) => {
          const value = Math.min(1, Math.max(0, (progress - start) / 0.16));
          this.style.setProperty(`--p${index + 1}`, value.toFixed(3));
        });
      }

      update() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          const rect = this.getBoundingClientRect();
          const distance = Math.max(1, rect.height - window.innerHeight);
          const progress = Math.min(1, Math.max(0, -rect.top / distance));
          const assembledOpacity = Math.min(1, Math.max(0, 1 - (progress - 0.08) / 0.28));
          const explodedOpacity = Math.min(1, Math.max(0, (progress - 0.16) / 0.28));
          this.style.setProperty('--exploded-progress', progress.toFixed(4));
          this.style.setProperty('--assembled-opacity', assembledOpacity.toFixed(3));
          this.style.setProperty('--exploded-opacity', explodedOpacity.toFixed(3));
          this.setSteps(progress);
          this.frame = null;
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
        if (this.frame) cancelAnimationFrame(this.frame);
      }
    }
  );
}

if (!customElements.get('hynox-offers')) {
  customElements.define(
    'hynox-offers',
    class HynoxOffers extends HTMLElement {
      connectedCallback() {
        this.choices = [...this.querySelectorAll('[data-hynox-offer]')];
        this.button = this.querySelector('[data-hynox-offer-add]');
        this.label = this.querySelector('[data-hynox-offer-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.error = this.querySelector('[data-hynox-offer-error]');
        this.update = this.update.bind(this);
        this.add = this.add.bind(this);
        this.choices.forEach((choice) => choice.addEventListener('change', this.update));
        this.button?.addEventListener('click', this.add);
        this.update();
      }

      update() {
        const choice = this.querySelector('[data-hynox-offer]:checked');
        if (!this.label || !this.button) return;
        if (!choice) {
          this.button.disabled = true;
          this.label.textContent = this.dataset.offerMode === 'standalone' ? 'Filtre indisponible' : 'Choisir une formule disponible';
          return;
        }
        const labels = {
          bottle: 'Ajouter HYNØX',
          'bottle-extra': 'Ajouter HYNØX + 1 filtre',
          'bottle-subscription': 'Choisir HYNØX + renouvellement',
          filter: 'Ajouter le filtre',
          'filter-subscription': 'Souscrire au renouvellement',
        };
        this.label.textContent = `${labels[choice.value]} · ${choice.dataset.price}`;
        const needsBottle = choice.value.startsWith('bottle');
        const needsFilter = choice.value !== 'bottle';
        const needsSellingPlan = choice.value.endsWith('subscription');
        const bottleAvailable = this.button.dataset.bottleAvailable === 'true';
        const filterAvailable = this.button.dataset.filterAvailable === 'true';
        const sellingPlanAvailable = Boolean(Number(this.button.dataset.sellingPlan));
        this.button.disabled = (needsBottle && !bottleAvailable) || (needsFilter && !filterAvailable) || (needsSellingPlan && !sellingPlanAvailable);
      }

      async add() {
        const choice = this.querySelector('[data-hynox-offer]:checked');
        if (!choice || !this.button) return;
        const bottleId = Number(this.button.dataset.bottleVariant);
        const filterId = Number(this.button.dataset.filterVariant);
        const sellingPlan = Number(this.button.dataset.sellingPlan);
        const items = [];
        if (choice.value.startsWith('bottle')) items.push({ id: bottleId, quantity: 1 });
        if (choice.value === 'bottle-extra' || choice.value === 'filter') items.push({ id: filterId, quantity: 1 });
        if (choice.value.endsWith('subscription')) {
          if (!sellingPlan) return this.showError('L’abonnement doit d’abord être activé dans Shopify.');
          items.push({ id: filterId, quantity: 1, selling_plan: sellingPlan });
        }

        this.button.disabled = true;
        this.button.classList.add('loading');
        this.spinner?.classList.remove('hidden');
        this.showError();
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const payload = { items };
        if (cart) {
          payload.sections = cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        try {
          const response = await fetch(`${routes.cart_add_url}.js`, {
            ...fetchConfig(),
            body: JSON.stringify(payload),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Ajout impossible.');
          if (cart) cart.renderContents(result);
          else window.location = routes.cart_url;
        } catch (error) {
          this.showError(error.message);
        } finally {
          this.button.disabled = false;
          this.button.classList.remove('loading');
          this.spinner?.classList.add('hidden');
        }
      }

      showError(message = '') {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }

      disconnectedCallback() {
        this.choices.forEach((choice) => choice.removeEventListener('change', this.update));
        this.button?.removeEventListener('click', this.add);
      }
    }
  );
}

if (!customElements.get('hynox-experience')) {
  customElements.define(
    'hynox-experience',
    class HynoxExperience extends HTMLElement {
      connectedCallback() {
        this.gallery = this.querySelector('[data-hynox-gallery]');
        this.mainImage = this.querySelector('.hynox-gallery__main');
        this.galleryMessage = this.querySelector('[data-hynox-gallery-message]');
        this.galleryKicker = this.querySelector('[data-hynox-gallery-kicker]');
        this.galleryTitle = this.querySelector('[data-hynox-gallery-title]');
        this.galleryCopy = this.querySelector('[data-hynox-gallery-copy]');
        this.thumbs = [...this.querySelectorAll('[data-hynox-gallery-thumb]')];
        this.hero = this.querySelector('[data-hynox-hero]');
        this.finalOffer = this.querySelector('[data-hynox-final]');
        this.sticky = this.querySelector('[data-hynox-sticky]');
        this.immersiveSections = [...this.querySelectorAll('.hynox-exploded')];
        this.selectImage = this.selectImage.bind(this);
        this.updateSticky = this.updateSticky.bind(this);

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.selectImage));
        window.addEventListener('scroll', this.updateSticky, { passive: true });
        window.addEventListener('resize', this.updateSticky, { passive: true });
        this.updateSticky();
      }

      selectImage(event) {
        const thumb = event.currentTarget;
        if (!this.mainImage) return;
        this.mainImage.src = thumb.dataset.imageSrc;
        this.mainImage.srcset = thumb.dataset.imageSrcset || '';
        this.mainImage.alt = thumb.dataset.imageAlt || 'HYNØX';
        this.mainImage.style.objectPosition = thumb.dataset.imagePosition || 'center';
        this.gallery?.style.setProperty('--gallery-bg', thumb.dataset.galleryBg || '#eee6df');
        this.mainImage.classList.toggle('is-cover', thumb.dataset.imageFit === 'cover');
        this.mainImage.classList.toggle('is-contain', thumb.dataset.imageFit !== 'cover');
        if (this.galleryMessage) {
          const hasMessage = Boolean(thumb.dataset.captionTitle);
          this.galleryKicker.textContent = thumb.dataset.captionKicker || '';
          this.galleryTitle.textContent = thumb.dataset.captionTitle || '';
          this.galleryCopy.textContent = thumb.dataset.captionCopy || '';
          this.galleryMessage.dataset.theme = thumb.dataset.captionTheme || 'dark';
          this.galleryMessage.dataset.position = thumb.dataset.captionPosition || 'bottom-left';
          this.galleryMessage.classList.toggle('is-hidden', !hasMessage);
        }
        this.thumbs.forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      }

      updateSticky() {
        if (!this.hero || !this.sticky) return;
        const heroPassed = this.hero.getBoundingClientRect().bottom < 80;
        const finalReached = this.finalOffer && this.finalOffer.getBoundingClientRect().top < window.innerHeight;
        const immersiveActive = this.immersiveSections.some((section) => {
          const rect = section.getBoundingClientRect();
          return rect.top < window.innerHeight * 0.82 && rect.bottom > window.innerHeight * 0.18;
        });
        this.sticky.classList.toggle('is-visible', heroPassed && !finalReached && !immersiveActive);
      }

      disconnectedCallback() {
        this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.selectImage));
        window.removeEventListener('scroll', this.updateSticky);
        window.removeEventListener('resize', this.updateSticky);
      }
    }
  );
}
